namespace RS.Localisation
{
    export type SimpleGetter = (locale: Locale) => string;
    export type PartsGetter = (locale: Locale) => TranslationPart[];
    export type ComplexGetter<T extends object> = (locale: Locale, substitutions: T) => string;
    export type BoundComplexGetter<T extends object> = { get: ComplexGetter<T>; getParts: PartsGetter; substitutions: T; };

    export type SimpleTranslationReference = { get: SimpleGetter; getParts: PartsGetter; };
    export type ComplexTranslationReference<T extends object> = { get: ComplexGetter<T>; getParts: PartsGetter; };

    export type LocalisableString = string | SimpleTranslationReference | BoundComplexGetter<object> | Error;

    /**
     * Generates a simple getter function for the specified key.
     * Used by generated code.
     * @param key
     */
    export function generateSimpleGetter(key: string): SimpleGetter
    {
        return function (locale: Locale)
        {
            if (locale == null) { return "NO LOCALE"; }
            return locale.getString(key, key);
        };
    }

    /**
     * Generates a complex getter function for the specified key.
     * Used by generated code.
     * @param key
     */
    export function generateComplexGetter<T extends { [key: string]: string | number }>(key: string, keyMap: Map<string>): ComplexGetter<T>
    {
        return function (locale: Locale, substitutions: T)
        {
            if (locale == null) { return "NO LOCALE"; }
            let str = locale.getString(key);
            if (str == null) { return key; }
            for (const subKey in substitutions)
            {
                str = str.replace(keyMap[subKey], `${substitutions[subKey]}`);
            }
            return str;
        };
    }

    /**
     * Binds the specified static substitutions to the specified complex translation key.
     * @param base
     * @param substitutions
     */
    export function bind<T extends object>(base: ComplexTranslationReference<T>, substitutions: T): BoundComplexGetter<T>
    {
        const bound: BoundComplexGetter<T> = { get: base.get, getParts: base.getParts, substitutions: {} as T };
        for (const key in substitutions)
        {
            bound.substitutions[key] = substitutions[key];
        }
        return bound;
    }

    /**
     * Resolves the specified LocalisableString into a string.
     * @param locale
     * @param str
     */
    export function resolveLocalisableString(locale: Locale, str: LocalisableString): string
    {
        if (Is.string(str))
        {
            return str;
        }
        else if (Is.boundComplexGetter(str))
        {
            return str.get(locale, str.substitutions);
        }
        else if (str instanceof Error)
        {
            return str.stack || str.message;
        }
        else
        {
            return str.get(locale);
        }
    }

    export interface TranslationPart
    {
        kind: TranslationPart.Kind;
        value: string;
    }

    export namespace TranslationPart
    {
        export enum Kind
        {
            Text,
            Variable,
            Protected
        }
    }

    interface TokenRule
    {
        regex: RegExp;
        group: number;
        kind: TranslationPart.Kind;
    }
    const tokenRules: TokenRule[] =
    [
        { regex: /\{([a-zA-Z0-9_]+)\}/, group: 1, kind: TranslationPart.Kind.Variable },
        { regex: /(\|\|?\|?)([^\|]+)\1/, group: 2, kind: TranslationPart.Kind.Protected }
    ];

    /**
     * Breaks down a translated string into parts.
     * @param str
     */
    export function getParts(str: string): TranslationPart[]
    {
        const result: TranslationPart[] = [];
        let remainingStr = str;
        while (remainingStr.length > 0)
        {
            let lowestMatch = remainingStr.length;
            let matchRule: TokenRule | null = null;
            let matchContent: string | null = null;
            let matchLength: number | null = null;
            for (const rule of tokenRules)
            {
                const match = rule.regex.exec(remainingStr);
                if (match)
                {
                    if (match.index < lowestMatch)
                    {
                        lowestMatch = match.index;
                        matchRule = rule;
                        matchContent = match[rule.group];
                        matchLength = match[0].length;
                    }
                }
            }
            if (lowestMatch > 0)
            {
                result.push({
                    kind: TranslationPart.Kind.Text,
                    value: remainingStr.substr(0, lowestMatch)
                });
                remainingStr = remainingStr.substr(lowestMatch);
            }
            if (matchRule)
            {
                result.push({
                    kind: matchRule.kind,
                    value: matchContent
                });
                remainingStr = remainingStr.substr(matchLength);
            }
        }
        return result;
    }

    export namespace SpecialChars
    {
        export const NonBreakingSpace = "\u00A0";
    }

    const defaultReduceFunc: (a: string, b: string) => string = (a, b) => `${a}\n${b}`;

    /**
     * Combines an array of localisable strings into a single LocalisableString.
     */
    export function reduce(localisations: LocalisableString[], reduceFunc?: (a: string, b: string) => string): LocalisableString;
    /**
     * Combines an array of localisable strings and complex translation references into a single complex translation reference.
     */
    export function reduce<TSubs extends object>(localisations: (LocalisableString | ComplexTranslationReference<Partial<TSubs>>)[], reduceFunc?: (a: string, b: string) => string): ComplexTranslationReference<TSubs>;
    export function reduce(localisations: (LocalisableString | ComplexTranslationReference<object>)[], reduceFunc: (a: string, b: string) => string = defaultReduceFunc): LocalisableString | ComplexTranslationReference<object>
    {
        if (localisations.length === 0) { return ""; }

        function resolve(locale: Locale, localisation: LocalisableString | ComplexTranslationReference<object>, substitutions: object)
        {
            if ((localisation as ComplexTranslationReference<object>).get)
            {
                const complexLocalisation = localisation as ComplexTranslationReference<object>;
                return complexLocalisation.get(locale, substitutions);
            }

            return resolveLocalisableString(locale, localisation as LocalisableString);
        }

        const composite: RS.Localisation.ComplexTranslationReference<object> =
        {
            get: (locale: RS.Localisation.Locale, substitutions?: object) => localisations
                .map((localisation) => resolve(locale, localisation, substitutions))
                .reduce((a: string, b: string) => reduceFunc(a, b)),
            getParts: (locale: Locale) => getParts(composite.get(locale, {}))
        };

        return composite;
    }
}
